import { useEffect, useRef, useState } from 'react';
import { request } from '../api';
import { Id, TextInput } from './kit';

/**
 * Выбор клиента поиском, а не вводом ULID.
 *
 * Формы слияния, разделения, согласий, карточка лояльности и фильтр журнала
 * отправок требовали вручную ввести двадцатишестисимвольный идентификатор без
 * поиска, автодополнения и подсказки формата. Откуда администратор его
 * возьмёт — не объяснялось нигде.
 */
export interface CustomerHit {
  readonly id: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly status: string;
}

interface PlateResolution {
  readonly customer?: { id: string; displayName: string | null; phone: string | null; status: string } | null;
}

const PHONE_LIKE = /^[+\d\s()-]{4,}$/;

async function search(query: string): Promise<readonly CustomerHit[]> {
  const value = query.trim();
  if (value.length < 3) return [];

  // Сервер ищет либо по телефону, либо по номеру машины. Что перед нами —
  // видно по самой строке, отдельного поля выбора человеку не нужно.
  if (PHONE_LIKE.test(value)) {
    return request<readonly CustomerHit[]>(
      'GET',
      `/api/v1/customers?phone=${encodeURIComponent(value)}`,
    );
  }

  const resolution = await request<PlateResolution>(
    'GET',
    `/api/v1/customers?plate=${encodeURIComponent(value)}`,
  );
  return resolution.customer ? [resolution.customer as CustomerHit] : [];
}

export function CustomerPicker({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (id: string, hit?: CustomerHit) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<readonly CustomerHit[]>([]);
  const [picked, setPicked] = useState<CustomerHit | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (value === '') setPicked(null);
  }, [value]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 3) {
      setHits([]);
      return undefined;
    }
    timer.current = setTimeout(() => {
      setBusy(true);
      search(query)
        .then(setHits)
        .catch(() => setHits([]))
        .finally(() => setBusy(false));
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);

  if (picked) {
    return (
      <div className="flex items-center justify-between gap-2 rounded border border-line-strong bg-sunken px-2.5 py-1.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-ink">
            {picked.displayName ?? 'Без имени'}
          </div>
          <div className="truncate text-xs text-ink-muted">
            {picked.phone ?? 'телефон не указан'} · <Id value={picked.id} />
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm text-brand-700 hover:underline"
          onClick={() => {
            setPicked(null);
            setQuery('');
            onChange('');
          }}
        >
          Сменить
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <TextInput
        value={query}
        disabled={disabled}
        placeholder={placeholder ?? 'Телефон или номер автомобиля'}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.trim().length >= 3 ? (
        <div className="absolute z-20 mt-1 w-full rounded border border-line bg-surface shadow-lg">
          {busy ? (
            <p className="px-3 py-2 text-sm text-ink-muted">Ищем…</p>
          ) : hits.length === 0 ? (
            <p className="px-3 py-2 text-sm text-ink-muted">Никого не нашли</p>
          ) : (
            hits.map((hit) => (
              <button
                key={hit.id}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-sunken"
                onClick={() => {
                  setPicked(hit);
                  setQuery('');
                  setHits([]);
                  onChange(hit.id, hit);
                }}
              >
                <span className="font-medium text-ink">{hit.displayName ?? 'Без имени'}</span>
                <span className="ml-2 text-xs text-ink-muted">{hit.phone ?? '—'}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export interface MultiOption {
  readonly value: string;
  readonly title: string;
  readonly note?: string | null;
  /** Значение показывается, но выбрать его нельзя — с объяснением в `note`. */
  readonly usable?: boolean;
}

/**
 * Выбор нескольких значений галочками.
 *
 * Заменяет поля «через запятую». Такое поле не только требовало знать коды
 * наизусть — оно молча принимало неверные: правило с опечаткой сохранялось и
 * не срабатывало ни разу, без единого слова о причине.
 *
 * Пустой выбор всюду означает «без ограничения», и это написано под списком, а
 * не подразумевается.
 */
export function MultiPicker({
  options,
  value,
  onChange,
  emptyMeans,
  loading,
}: {
  options: readonly MultiOption[];
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  emptyMeans: string;
  loading?: boolean;
}) {
  const chosen = new Set(value);

  function toggle(option: MultiOption): void {
    if (option.usable === false) return;
    const next = chosen.has(option.value)
      ? value.filter((v) => v !== option.value)
      : [...value, option.value];
    onChange(next);
  }

  if (loading) {
    return <p className="text-sm text-ink-muted">Загружаем…</p>;
  }

  if (options.length === 0) {
    return <p className="text-sm text-ink-muted">Выбирать не из чего — список пуст.</p>;
  }

  return (
    <div>
      <div className="max-h-44 space-y-0.5 overflow-y-auto rounded border border-line-strong p-1.5">
        {options.map((option) => {
          const off = option.usable === false;
          return (
            <label
              key={option.value}
              className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${
                off ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-sunken'
              }`}
            >
              <input
                type="checkbox"
                disabled={off}
                checked={chosen.has(option.value)}
                onChange={() => toggle(option)}
              />
              <span className="text-ink">{option.title}</span>
              {option.note ? (
                <span className="text-xs text-ink-muted">· {option.note}</span>
              ) : null}
            </label>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {chosen.size === 0 ? emptyMeans : `Выбрано: ${chosen.size}`}
      </p>
    </div>
  );
}

export interface VehicleHit {
  readonly id: string;
  readonly plate: string;
  /** Владелец на сегодня, если связь открыта. */
  readonly ownerName: string | null;
}

interface VehicleResolution {
  readonly vehicle?: { id: string; plate: string } | null;
  readonly customer?: { displayName: string | null } | null;
}

/**
 * Выбор автомобиля по номеру.
 *
 * Проверка покрытия подпиской и разрешение конфликта идентификации просили
 * «идентификатор автомобиля» — при том, что заголовок панели обещал проверку
 * *по номеру*. Номер написан на машине, идентификатор не написан нигде.
 *
 * Сервер уже умеет разрешать номер в машину и владельца (`/customers?plate=`) —
 * тот же вызов, что и у поиска клиента, только берётся из него другая половина
 * ответа.
 */
export function VehiclePicker({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (vehicleId: string, hit?: VehicleHit) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [hit, setHit] = useState<VehicleHit | null>(null);
  const [state, setState] = useState<'idle' | 'busy' | 'missing'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (value === '') setHit(null);
  }, [value]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const plate = query.trim();
    if (plate.length < 3) {
      setState('idle');
      return undefined;
    }
    timer.current = setTimeout(() => {
      setState('busy');
      request<VehicleResolution>('GET', `/api/v1/customers?plate=${encodeURIComponent(plate)}`)
        .then((res) => {
          if (!res.vehicle) {
            setState('missing');
            return;
          }
          const found: VehicleHit = {
            id: res.vehicle.id,
            plate: res.vehicle.plate,
            ownerName: res.customer?.displayName ?? null,
          };
          setHit(found);
          setQuery('');
          setState('idle');
          onChange(found.id, found);
        })
        .catch(() => setState('missing'));
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // onChange меняется каждый рендер у вызывающих — в зависимости не берём,
    // иначе поиск перезапускался бы на каждое перерисовывание страницы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  if (hit) {
    return (
      <div className="flex items-center justify-between gap-2 rounded border border-line-strong bg-sunken px-2.5 py-1.5">
        <div className="min-w-0">
          <div className="truncate font-mono text-sm font-medium text-ink">{hit.plate}</div>
          <div className="truncate text-xs text-ink-muted">
            {hit.ownerName ?? 'владелец не привязан'} · <Id value={hit.id} />
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm text-brand-700 hover:underline"
          onClick={() => {
            setHit(null);
            setQuery('');
            onChange('');
          }}
        >
          Сменить
        </button>
      </div>
    );
  }

  return (
    <div>
      <TextInput
        value={query}
        disabled={disabled}
        className="font-mono"
        placeholder={placeholder ?? 'Номер автомобиля'}
        onChange={(e) => setQuery(e.target.value)}
      />
      {state === 'busy' ? <p className="mt-1 text-xs text-ink-muted">Ищем…</p> : null}
      {state === 'missing' ? (
        <p className="mt-1 text-xs text-warn">Машины с таким номером в базе нет</p>
      ) : null}
    </div>
  );
}

export interface EmployeeOption {
  readonly employeeId: string;
  readonly displayName: string;
}

/**
 * Ответственного по обращению назначали вводом «идентификатора сотрудника».
 * Список сотрудников объекта уже отдаёт касса — берём его.
 */
/**
 * Имена сотрудников объекта по идентификатору.
 *
 * Карточка обращения показывала «Сотрудник» и «Ответственный» короткими
 * идентификаторами: узнать по ним, кто это, было нельзя, а именно на этот
 * вопрос строки и отвечают. Список тот же, что у выбора ответственного.
 */
export function useEmployeeNames(siteId: string | null): ReadonlyMap<string, string> {
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    if (!siteId) {
      setNames(new Map());
      return;
    }
    request<readonly EmployeeOption[]>('GET', '/api/v1/pos/cashiers')
      .then((list) => setNames(new Map(list.map((e) => [e.employeeId, e.displayName]))))
      .catch(() => setNames(new Map()));
  }, [siteId]);

  return names;
}

export function EmployeePicker({
  value,
  onChange,
  siteId,
  emptyLabel,
}: {
  value: string;
  onChange: (employeeId: string) => void;
  siteId: string | null;
  emptyLabel: string;
}) {
  const [options, setOptions] = useState<readonly EmployeeOption[]>([]);

  useEffect(() => {
    if (!siteId) {
      setOptions([]);
      return;
    }
    // Объект берётся из заголовка x-moika-site, который ставит общий request().
    request<readonly EmployeeOption[]>('GET', '/api/v1/pos/cashiers')
      .then(setOptions)
      .catch(() => setOptions([]));
  }, [siteId]);

  if (options.length === 0) {
    return (
      <TextInput
        value={value}
        placeholder={emptyLabel}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <select
      className="w-full rounded border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink outline-none focus:border-brand-500"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {options.map((option) => (
        <option key={option.employeeId} value={option.employeeId}>
          {option.displayName}
        </option>
      ))}
    </select>
  );
}

/**
 * Названия объектов по идентификатору.
 *
 * Списки визитов, заказов и платежей показывали объект хвостом ULID вида
 * `…6DA7G836`. Объект — вещь именованная: у него есть название на вывеске и
 * короткий код, по которым его знают и в сети, и на самом объекте. Хвост
 * идентификатора не совпадает ни с чем из этого.
 *
 * Список объектов сети короткий и меняется раз в квартал, поэтому читается
 * целиком и один раз на монтирование.
 */
export function useSiteNames(): (siteId: string | null | undefined) => string | null {
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    request<readonly { id: string; code: string; name: string }[]>('GET', '/api/v1/sites')
      .then((list) => setNames(new Map(list.map((s) => [s.id, `${s.name} · ${s.code}`]))))
      .catch(() => setNames(new Map()));
  }, []);

  return (siteId) => (siteId ? (names.get(siteId) ?? null) : null);
}
